import React from 'react';
import { Link } from 'react-router-dom';

export default function DenmarkMap() {
  const markers = [
    // HOVEDKONTOR
    {
      name: "Hovedkontor (Ryomgård)",
      description: "Vores base på Djursland – herfra kører vi ud til hele landet.",
      x: "52%",
      y: "43%",
      isBase: true,
    },
    // SJÆLLAND
    {
      name: "Sjælland & København",
      description: "Høj aktivitet – her ligger ca. 80% af vores opgaver inden for facaderens, historiske ejendomme og industri.",
      x: "81%",
      y: "66%",
    },
    // KBH, Roskilde, Hillerød, Hellerup, Sorø
    {
      name: "København",
      description: "Præcisionslaserrens af historiske ejendomme, facader og industri i hovedstaden.",
      x: "84%",
      y: "66%",
    },
    {
      name: "Hellerup",
      description: "Skånsom afrensning af eksklusive facader, villaer og træværk.",
      x: "84%",
      y: "63%",
    },
    {
      name: "Hillerød",
      description: "Laserrens af nybyggeri, erhvervsejendomme og historiske rammer i Nordsjælland.",
      x: "81%",
      y: "59%",
    },
    {
      name: "Roskilde",
      description: "Effektiv afrensning af beton, murværk og private boliger i Roskilde-området.",
      x: "78%",
      y: "68%",
    },
    {
      name: "Sorø",
      description: "Skånsom laserrens af herregårde, historiske bygninger og industri på Midtsjælland.",
      x: "72%",
      y: "71%",
    },
    // FYN
    {
      name: "Fyn (Odense & omegn)",
      description: "Skånsom laserrens af historiske byhuse, bindingsværk, facader og maritime miljøer.",
      x: "46%",
      y: "76%",
    },
    // ØSTJYLLAND
    {
      name: "Østjylland (Aarhus / Randers)",
      description: "Hurtig udrykning til facaderens, beton og graffiti på erhverv og private ejendomme.",
      x: "41%",
      y: "51%",
    },
    // VESTJYLLAND
    {
      name: "Vestjylland (Esbjerg / Ringkøbing)",
      description: "Laserrens af sommerhuse, træværk, vindblæste facader samt rustfjernelse på havneudstyr.",
      x: "16%",
      y: "64%",
    },
    // NORDJYLLAND
    {
      name: "Nordjylland (Aalborg & omegn)",
      description: "Effektiv afrensning af industrianlæg, skibsværfter, murværk og betonkonstruktioner.",
      x: "42%",
      y: "18%",
    },
    // SØNDERJYLLAND
    {
      name: "Sønderjylland (Haderslev / Aabenraa)",
      description: "Skånsom afrensning af historiske bykerner, erhvervsejendomme og transportmateriel.",
      x: "26%",
      y: "82%",
    },
  ];

  // Helper to align tooltips dynamically based on their horizontal position
  const getTooltipClass = (x) => {
    const xVal = parseFloat(x);
    if (xVal > 68) {
      return "absolute bottom-full right-0 mb-2 w-48 bg-zinc-900 text-white text-xs rounded-lg p-2.5 shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300 z-20";
    }
    if (xVal < 32) {
      return "absolute bottom-full left-0 mb-2 w-48 bg-zinc-900 text-white text-xs rounded-lg p-2.5 shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300 z-20";
    }
    return "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-zinc-900 text-white text-xs rounded-lg p-2.5 shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300 z-20";
  };

  const getTooltipArrowClass = (x) => {
    const xVal = parseFloat(x);
    if (xVal > 68) {
      return "absolute top-full right-4 w-2 h-2 bg-zinc-900 rotate-45";
    }
    if (xVal < 32) {
      return "absolute top-full left-4 w-2 h-2 bg-zinc-900 rotate-45";
    }
    return "absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-900 rotate-45";
  };

  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 md:p-12 shadow-xl relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-brand-green/5 blur-3xl pointer-events-none" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Text Content */}
        <div className="lg:col-span-6 flex flex-col gap-5">
          <div className="inline-flex items-center gap-2 bg-brand-green/10 border border-brand-green/20 px-3.5 py-1.5 rounded-full text-brand-green text-xs font-semibold uppercase tracking-wider w-fit">
            <i className="fa-solid fa-location-dot" /> Mobil laserafrensning
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-zinc-900 leading-tight">
            Vi dækker hele Danmark
          </h2>
          <div className="space-y-4 text-zinc-600 leading-relaxed text-sm md:text-base">
            <p className="text-zinc-700 leading-relaxed">
              Ecolaser har base på Djursland og udfører mobile opgaver med laserrens og laserafrensning i hele Danmark. Udstyret bringes direkte frem til byggepladsen, ejendommen, produktionsstedet eller den tekniske installation, hvor arbejdet skal udføres.
            </p>

            {/* Opdelt i overskuelige, luftige fokusfelter */}
            <div className="grid grid-cols-1 gap-3.5 pt-1">
              {/* Industri & Aktører */}
              <div className="bg-white rounded-xl p-4 md:p-4.5 border border-zinc-200/90 shadow-xs flex items-start gap-3.5 hover:border-zinc-300 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5 text-zinc-700">
                  <i className="fa-solid fa-industry text-sm" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-900">
                    Byggeri, Industri & Rådgivning
                  </h4>
                  <p className="text-xs md:text-sm text-zinc-600 leading-relaxed">
                    Vi løser blandt andet opgaver for bygherrer, entreprenører, ejendomsselskaber, rådgivere, virksomheder og offentlige aktører. Ved industrielle projekter kan laserrens anvendes på blandt andet procesudstyr, støbeforme, metal og tekniske overflader, når materialet egner sig til behandlingen. Det indgår blandt andet i vores arbejde med <Link to="/ydelser/industri-skadeservice" className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 font-medium hover:text-brand-green transition-colors">laserrens til industri og skadeservice</Link>.
                  </p>
                </div>
              </div>

              {/* Geografi & Byer */}
              <div className="bg-white rounded-xl p-4 md:p-4.5 border border-zinc-200/90 shadow-xs flex items-start gap-3.5 hover:border-zinc-300 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-brand-green flex items-center justify-center shrink-0 mt-0.5">
                  <i className="fa-solid fa-map-location-dot text-sm" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-900">
                    Særligt fokus: Aarhus, Odense & København
                  </h4>
                  <p className="text-xs md:text-sm text-zinc-600 leading-relaxed">
                    Vi kører til projekter i hele landet med særligt fokus på Aarhus, Odense og København samt byer med bevaringsværdige og fredede bygninger, hvor en skånsom tilgang til afrensningen er afgørende.
                  </p>
                </div>
              </div>
            </div>

            {/* Tryghed / Kvalitetsvurdering */}
            <div className="bg-emerald-50/60 border border-brand-green/25 p-4 rounded-xl text-xs md:text-sm text-zinc-800 flex items-start gap-3 shadow-xs">
              <i className="fa-solid fa-circle-check text-brand-green mt-0.5 shrink-0 text-base" />
              <p className="leading-relaxed">
                Ved større eller komplekse opgaver analyserer vi altid materialets beskaffenhed og belægning for at sikre den helt præcise laserkalibrering, ligesom vi afdækker de praktiske rammer og adgangsforhold før opstart.
              </p>
            </div>
          </div>

          {/* Stats / Badges */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-200 mt-2">
            <div>
              <div className="text-2xl md:text-3xl font-extrabold text-zinc-900">80%</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Opgaver på Sjælland</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-extrabold text-zinc-900">100%</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Mobil & Selvkørende</div>
            </div>
          </div>
        </div>

        {/* Map Visualization */}
        <div className="lg:col-span-6 flex justify-center relative">
          <div className="relative max-w-xl w-full aspect-square flex items-center justify-center">
            {/* The Original SVG Map */}
            <img
              src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/dk-kort.png"
              alt="Danmarkskort"
              className="w-full h-full object-contain opacity-85 mix-blend-multiply"
              onLoad={(e) => console.log("DK_KORT_DIMENSIONS:", e.target.naturalWidth, "x", e.target.naturalHeight)}
            />

            {/* Markers */}
            {markers.map((marker, idx) => (
              <div
                key={idx}
                className="absolute group z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: marker.x, top: marker.y }}
              >
                {/* Pulsing Ring 1 */}
                <span 
                  className={`absolute -left-2 -top-2 w-9 h-9 rounded-full animate-ping ${
                    marker.isBase ? 'bg-emerald-500/40' : 'bg-brand-green/40'
                  }`}
                  style={{ 
                    animationDelay: `${(idx % 4) * 0.5}s`, 
                    animationDuration: '2.5s' 
                  }}
                />
                
                {/* Pulsing Ring 2 (staggered) */}
                <span 
                  className={`absolute -left-4 -top-4 w-13 h-13 rounded-full animate-ping ${
                    marker.isBase ? 'bg-emerald-500/20' : 'bg-brand-green/20'
                  }`}
                  style={{ 
                    animationDelay: `${((idx % 4) * 0.5) + 0.6}s`, 
                    animationDuration: '2.5s' 
                  }}
                />
                
                {/* Dot */}
                <button
                  className={`relative rounded-full border-2 border-white shadow-lg transition-all duration-300 group-hover:scale-120 cursor-pointer flex items-center justify-center ${
                    marker.isBase 
                      ? 'w-7 h-7 bg-emerald-600 ring-4 ring-emerald-500/30' 
                      : 'w-5.5 h-5.5 bg-brand-green ring-4 ring-brand-green/20'
                  }`}
                  aria-label={marker.name}
                >
                  {marker.isBase && <i className="fa-solid fa-star text-white text-[10px]" />}
                </button>

                {/* Tooltip */}
                <div className={getTooltipClass(marker.x)}>
                  <div className="font-bold text-white flex items-center gap-1.5">
                    {!marker.isBase && <span className="w-2 h-2 rounded-full bg-brand-green inline-block" />}
                    {marker.isBase && <i className="fa-solid fa-star text-amber-400 text-[10px]" />}
                    {marker.name}
                  </div>
                  <div className="text-zinc-400 text-[10px] mt-0.5 leading-normal">{marker.description}</div>
                  {/* Arrow */}
                  <div className={getTooltipArrowClass(marker.x)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
